import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import usePost from "../customHooks/usePost";
import config from "../services/config.json";
import http from "../services/httpServices";
import { getTokenSession } from "../utils/common";
import "./UserForm.css";
import { MenuActive } from "../context/MenuActiveContext";

const UserFormFields = ({ user, isEdit }) => {
  const navigate = useNavigate();
  const [response, submit] = usePost();
  const [values, setValues] = useState({ name: "", email: "", address: "", location: "", phone: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (user) {
      setValues({
        name: user.name || "",
        email: user.email || "",
        address: user.address || "",
        location: user.location || "",
        phone: user.phone || "",
        password: "",
      });
      setProfileImage(user.profile_image || null);
    }
  }, [user]);

  useEffect(() => {
    if (profileImage instanceof File) {
      const objectUrl = URL.createObjectURL(profileImage);
      setImagePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setImagePreview(profileImage || "");
  }, [profileImage]);

  useEffect(() => {
    if (!response.data) return;
    if (response.data.status === "false" || response.data.status === "error") {
      toast.error(response.data.message || "Unable to save user");
      return;
    }
    toast.success(response.data.message || (isEdit ? "User updated" : "User created"));
    navigate(`/${config.demo}users`);
  }, [response.data, isEdit, navigate]);

  useEffect(() => {
    if (response.error) {
      const message = response.error.response?.data?.message || "Unable to save user";
      if (response.error.response?.data?.errors) setErrors(response.error.response.data.errors);
      toast.error(message);
    }
  }, [response.error]);

  const updateField = (event) => {
    setValues((current) => ({ ...current, [event.target.name]: event.target.value }));
    setErrors((current) => ({ ...current, [event.target.name]: undefined }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!values.name.trim()) nextErrors.name = "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) nextErrors.email = "Enter a valid email";
    if (!isEdit && !values.password) nextErrors.password = "Password is required";
    if (!isEdit && values.password.length < 6) nextErrors.password = "Password must be at least 6 characters";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const formData = new FormData();
    formData.append("name", values.name.trim());
    formData.append("email", values.email.trim());
    formData.append("address", values.address.trim());
    formData.append("location", values.location.trim());
    formData.append("phone", values.phone.trim());
    formData.append("password", values.password);
    if (profileImage instanceof File) formData.append("profile_image", profileImage);
    submit(`dashboard/${isEdit ? `updateUser/${user.id}` : "storeUser"}`, formData);
  };

  return (
    <main className="user-form-page">
      <div className="user-form-page__content">
        <Link to={`/${config.demo}users`} className="user-form-back">
          <img src={require("../dist/webImages/arrow-left2.png")} alt="" />
          <span>Back to User Management</span>
        </Link>
        <section className="user-form-card">
          <header className="user-form-card__header">
            <h1>{isEdit ? "Edit User" : "Create User"}</h1>
            <p>{isEdit ? "Update the account details below." : "Add a new user account to your dashboard."}</p>
          </header>
          <form className="user-form" onSubmit={handleSubmit} noValidate>
            <div className="user-form__field">
              <label htmlFor="user-name">Name</label>
              <input id="user-name" name="name" value={values.name} onChange={updateField} autoComplete="name" aria-invalid={Boolean(errors.name)} />
              {errors.name && <small className="text-red-600" role="alert">{errors.name}</small>}
            </div>
            <div className="user-form__field">
              <label htmlFor="user-email">Email</label>
              <input id="user-email" name="email" type="email" value={values.email} onChange={updateField} readOnly={isEdit} autoComplete="email" aria-invalid={Boolean(errors.email)} />
              {errors.email && <small className="text-red-600" role="alert">{errors.email}</small>}
            </div>
            <div className="user-form__field">
              <label htmlFor="user-phone">Phone</label>
              <input id="user-phone" name="phone" type="tel" value={values.phone} onChange={updateField} autoComplete="tel" />
            </div>
            <div className="user-form__field">
              <label htmlFor="user-location">Location</label>
              <input id="user-location" name="location" value={values.location} onChange={updateField} autoComplete="country-name" />
            </div>
            <div className="user-form__field user-form__field--wide">
              <label htmlFor="user-address">Address</label>
              <textarea id="user-address" name="address" value={values.address} onChange={updateField} rows={3} autoComplete="street-address" />
            </div>
            {/* <div className="user-form__field user-form__field--wide">
              <label htmlFor="user-profile-image">Profile image</label>
              <div className="user-form__image-picker">
                {imagePreview ? (
                  <img
                    className="user-form__image-preview"
                    src={imagePreview}
                    alt="User profile preview"
                    onError={(event) => { event.currentTarget.src = require("../dist/webImages/noimage.jpg"); }}
                  />
                ) : (
                  <div className="user-form__image-placeholder" aria-hidden="true">{values.name.trim().charAt(0).toUpperCase() || "U"}</div>
                )}
                <div className="user-form__image-control">
                  <input
                    id="user-profile-image"
                    type="file"
                    accept="image/*"
                    onChange={(event) => setProfileImage(event.target.files?.[0] || profileImage)}
                  />
                  <small>Choose a JPG, PNG, or other image file. Optional.</small>
                </div>
              </div>
            </div> */}
            <div className="user-form__field user-form__field--wide user-form__field--password">
              <label htmlFor="user-password">Password</label>
              <div className="user-form__password-input">
                <input id="user-password" name="password" type={showPassword ? "text" : "password"} value={values.password} onChange={updateField} autoComplete="new-password" aria-invalid={Boolean(errors.password)} />
                <button
                  className="user-form__password-toggle"
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
                </button>
              </div>
              {isEdit && <small className="user-form__hint">Password field is sent even when left blank.</small>}
              {errors.password && <small className="text-red-600" role="alert">{errors.password}</small>}
            </div>
            <div className="user-form__actions">
              <button className="user-form__submit" type="submit" disabled={response.isLoading}>
                {response.isLoading ? "Saving..." : isEdit ? "Update User" : "Create User"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

const EditUserForm = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    http.get(`${config.apiEndPoint}dashboard/editUser/${id}`, {
      headers: { Authorization: `Bearer ${getTokenSession()}` },
    }).then((response) => {
      if (active) setData(response.data);
    }).catch(() => {
      if (active) setError(true);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [id]);

  if (loading) return <Loader />;
  if (error || !data) return <p role="alert">Unable to load this user. Please return to User Management and try again.</p>;
  const user = data.user || data.data?.user || data.data || data;
  return <UserFormFields user={user} isEdit />;
};

const UserForm = () => {
  const { id } = useParams();
  const { setMunuActiv } = useContext(MenuActive);

  useEffect(() => {
    setMunuActiv([true, "UserManagement"]);
  }, [setMunuActiv]);

  return id ? <EditUserForm id={id} /> : <UserFormFields isEdit={false} />;
};

export default UserForm;
